/**
 * Navigation menu configuration for the responsive top navigation system.
 *
 * This file is the single source of truth for menu items.
 * Add new pages here by extending the arrays.
 *
 * Permission flags use SC_Permissions.inputKeys constants for type safety.
 * See SC_Permissions.ts for available permission flags.
 */

import {SC_NavigationItem} from "../../util/api/SC_NavigationItem";
import {SC_Permissions} from "../../util/api/SC_Permissions";
import {SC_Department} from "../../util/api/enums/SC_Department";

/**
 * Main navigation groups for organizing menu items.
 */
export const NAVIGATION_GROUPS = {
    MAIN: "Main",
    WORK_MANAGEMENT: "Work Management",
    SCHEDULING: "Scheduling",
    REPORTS: "Reports",
    ADMIN: "Administration",
    UTILITIES: "Utilities",
};

/**
 * All navigation items mapped from existing routes in index.js.
 * Each item includes the required permission for visibility.
 * 
 * To add a new page, add an entry here with the appropriate permission.
 * New pages should also add their route to index.js first.
 */
export const navigationItems: SC_NavigationItem[] = [
    {
        title: "Dashboard",
        path: "/dashboard/",
        icon: "fa-home",
        permission: SC_Permissions.inputKeys.scheduleService,
        group: NAVIGATION_GROUPS.MAIN,
    },

    {
        title: "Workorders",
        path: "/neo-list-workorders/",
        icon: "fa-tasks",
        permission: SC_Permissions.inputKeys.scheduleService,
        group: NAVIGATION_GROUPS.WORK_MANAGEMENT,
    },

    {
        title: "Calendar",
        path: "/schedulers-calendar/",
        icon: "fa-calendar-check",
        permission: SC_Permissions.inputKeys.scheduleService,
        group: NAVIGATION_GROUPS.SCHEDULING,
        departments: [
            SC_Department.fromAPI(SC_Department.SCHEDULING)
        ]
    },
    {
        title: "Ops",
        path: "/operations-calendar/",
        icon: "fa-calendar-week",
        permission: SC_Permissions.inputKeys.scheduleService,
        group: NAVIGATION_GROUPS.SCHEDULING,
        departments: [
            SC_Department.fromAPI(SC_Department.OPERATIONS)
        ]
    },
    {
        title: "IVR",
        path: "/ivr-page/",
        icon: "fa-phone",
        permission: SC_Permissions.inputKeys.scheduleService,
        group: NAVIGATION_GROUPS.SCHEDULING,
        departments: [
            SC_Department.fromAPI(SC_Department.COMPLIANCE)
        ]
    },
    {
        title: "Compliance",
        path: "/compliance-page/",
        icon: "fa-phone",
        permission: SC_Permissions.inputKeys.scheduleService,
        group: NAVIGATION_GROUPS.SCHEDULING,
        departments: [
            SC_Department.fromAPI(SC_Department.COMPLIANCE)
        ]
    },

    {
        title: "Sites",
        path: "/neo-list-locations/",
        icon: "fa-map-marker-alt",
        permission: SC_Permissions.inputKeys.writeLocations,
        group: NAVIGATION_GROUPS.WORK_MANAGEMENT,
    },
    {
        title: "Crews",
        path: "/neo-list-crews/",
        icon: "fa-users",
        permission: SC_Permissions.inputKeys.writeCrews,
        group: NAVIGATION_GROUPS.WORK_MANAGEMENT,
    },

    {
        title: "Reports",
        path: "/reports/",
        icon: "fa-chart-bar",
        permission: SC_Permissions.inputKeys.viewReports,
        group: NAVIGATION_GROUPS.REPORTS,
        departments: [

        ]
    },

    // ==================== ADMIN ====================
    {
        title: "Users",
        path: "/user-management/",
        icon: "fa-user-cog",
        permission: SC_Permissions.inputKeys.writeLocations,
        group: NAVIGATION_GROUPS.ADMIN,
    },
].map(it => new SC_NavigationItem(it));

/**
 * Helper function to filter navigation items by permission.
 * @param items - Navigation items to filter
 * @param perms - SC_Permissions instance
 * @returns Filtered array of items user can see
 */
export function filterByPermission(
    items: SC_NavigationItem[],
    perms: any
): SC_NavigationItem[] {
    return items.filter((item) => item.matchesPermission(perms));
}

export function filterMine(
    items: SC_NavigationItem[],
    perms: SC_Permissions,
): SC_NavigationItem[] {
    return items.filter((item) => item.matchesFilter(perms, perms.departments));
}

/**
 * Helper function to group navigation items by their group property.
 * @param items - Navigation items to group
 * @returns Object with group keys as properties containing arrays
 */
export function groupByNavigationGroup(
    items: SC_NavigationItem[]
): Record<string, SC_NavigationItem[]> {
    return items.reduce((acc, item) => {
        if (!acc[item.group]) {
            acc[item.group] = [];
        }
        acc[item.group].push(item);
        return acc;
    }, {} as Record<string, SC_NavigationItem[]>);
}

/**
 * Helper function to get all groups with at least one visible item.
 * @param items - Navigation items to evaluate
 * @param perms - SC_Permissions instance
 * @returns Array of group keys that have visible items
 */
export function getVisibleGroups(
    items: SC_NavigationItem[],
    perms: any
): string[] {
    const grouped = groupByNavigationGroup(items);
    const visible: string[] = [];

    Object.entries(grouped).forEach(([group, items]) => {
        const visibleInGroup = items.filter((item) => item.matchesPermission(perms));
        if (visibleInGroup.length > 0) {
            visible.push(group);
        }
    });

    return visible;
}

/**
 * Helper function to get all unique permission requirements.
 * @param items - Navigation items to analyze
 * @returns Array of unique permission strings
 */
export function getRequiredPermissions(
    items: SC_NavigationItem[]
): string[] {
    const permissions = new Set<string>();
    items.forEach((item) => {
        if (item.permission) {
            permissions.add(item.permission);
        }
    });
    return Array.from(permissions).sort();
}
